const PAST_OVERDUE_THRESHOLD = 7; // days

const isAssignmentClosed = assignment => {
	let edd = assignment.edd || 0;
	let closed = assignment.closed || 0;
	return closed && edd < closed;
};

/**
 * Checks if an assignment is submitted or past it's deadline
 */
const isAssignmentSubmitted = assignment => {
	let now = new Date().getTime() / 1000;
	return isAssignmentClosed(assignment) || assignment.submitted || (assignment.ed && assignment.ed < now);
};

/**
 * Checks if an assignment is due past a threshold (X days)
 * @param {number} [dueDate] Unix timestamp in seconds
 * @param {number} threshold Number of days
 */
const isAssignmentPastOverDue = (dueDate, threshold = PAST_OVERDUE_THRESHOLD) => {
	if (!dueDate) return false;
	const thresholdInMS = threshold * 24 * 60 * 60 * 1000;

	// adjust due date time to be 12:59pm @ local time
	const dueDateEndOfDay = new Date(dueDate * 1000).setHours(23, 59, 59, 999);
	const dueDateWithThreshold = dueDateEndOfDay + thresholdInMS;
	const now = Date.now();

	return dueDateWithThreshold < now;
};

/**
 * Get assignment submitted time or deadline time if past deadline
 */
const getAssignmentSubmittedTime = assignment => {
	let now = new Date().getTime() / 1000;
	let timeSubmitted = null;
	if (assignment.submitted) {
		timeSubmitted = assignment.timeSubmitted;
	} else if (assignment.ed && assignment.ed < now) {
		timeSubmitted = assignment.ed;
	}
	return timeSubmitted;
};
/**
 * Get display time for assignment list
 */
const getAssignmentDisplayTime = assignment => {
	if (isAssignmentSubmitted(assignment)) {
		// Use submitted over deadline over creation
		return getAssignmentSubmittedTime(assignment);
	}
	// If not submitted, use due date over deadline over reassigned over start date over creation
	return assignment.edd || assignment.ed || assignment.rts || assignment.d || assignment.c;
};

/**
 * Sort assignments by assignment submitted date, due date/deadline, or start date/creation time.
 */
const sortAssignmentsBySubmittedThenCreationTime = (a, b) => {
	let aIsSubmitted = isAssignmentSubmitted(a);
	let bIsSubmitted = isAssignmentSubmitted(b);

	// if both are submitted, then sort by timeSubmitted (most recent submission first)
	if (aIsSubmitted && bIsSubmitted) {
		let aTimeSubmitted = getAssignmentSubmittedTime(a);
		let bTimeSubmitted = getAssignmentSubmittedTime(b);

		return bTimeSubmitted - aTimeSubmitted;
	}

	// between submitted & not submitted assignments
	// sort by not submitted assignments first
	if (aIsSubmitted && !bIsSubmitted) return 1;
	if (!aIsSubmitted && bIsSubmitted) return -1;

	const dueDateA = a.edd || a.ed;
	const dueDateB = b.edd || b.ed;
	const isPastOverDueA = isAssignmentPastOverDue(dueDateA);
	const isPastOverDueB = isAssignmentPastOverDue(dueDateB);

	// Sort assignments 7days+ over due to the end of the list in reverse order (oldest due date first);
	if (isPastOverDueA && !isPastOverDueB) return 1;
	if (!isPastOverDueA && isPastOverDueB) return -1;
	if (isPastOverDueA && isPastOverDueB) return dueDateA - dueDateB;

	// Show remaining items with due dates first
	// and reverse sort (oldest due date first)
	if (dueDateA && !dueDateB) return -1;
	if (!dueDateA && dueDateB) return 1;
	if (dueDateA && dueDateB) return dueDateA - dueDateB;

	// at this point, assignments are on equal footing submit-wise, so sort
	// by creation time in ASC order
	const startOrReassignDateA = a.rts || a.d || a.c,
		startOrReassignDateB = b.rts || b.d || b.c;

	return startOrReassignDateA - startOrReassignDateB;
};

module.exports = {
	sortAssignmentsBySubmittedThenCreationTime,
	getAssignmentDisplayTime,
	isAssignmentSubmitted
};
